import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { Href } from "@-/com.npmjs.expo-router";
import {
  PRIMARY_CONTROLS_STRIP_BACK_HIDDEN,
  PrimaryControlsStripBackContext,
  type PrimaryControlsStripBackAppShellPlacement,
  type PrimaryControlsStripBackRegistryEntry,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.back";
import { useNavigationDepth, useScopedBack } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import type { PrimaryControlsStripBackGate } from "#use-primary-controls-strip-back-gate";
import useLatest from "@-/com.npmjs.use-latest";
import { useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { Platform, Pressable, View } from "@-/com.npmjs.react-native";
import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";

const isWeb = Platform.OS === "web";

function isRenderableAsPlainLabel(value: ReactNode): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

/**
 * The app shell's back control: a glass circle the size of the More trigger, because on a nested
 * page it takes that trigger's slot (the More menu belongs to a layout's main pages only).
 */
function GlassBackControl({ onPress }: { onPress: () => void }) {
  const { diameter } = useToolbarGridMetrics();
  return (
    <ToolbarGlassSurface style={{ height: diameter, width: diameter }}>
      <Pressable
        accessibilityLabel="Back"
        accessibilityRole="button"
        className="size-full items-center justify-center"
        hitSlop={8}
        onPress={onPress}
        // Stable id for E2E across every placement; see `DefaultBackControl`.
        testID="primary-controls-strip-back-control"
      >
        <Icon className="text-foreground" name="FiChevronLeftBold" size={24} />
      </Pressable>
    </ToolbarGlassSurface>
  );
}

function DefaultBackControl({ label, onPress }: { label?: ReactNode; onPress: () => void }) {
  const hasLabel = label !== undefined && label !== null;

  return (
    <Pressable
      accessibilityLabel="Back"
      accessibilityRole="button"
      // With no label the control pins to a fixed `size-10` (40px) box, so the
      // control's footprint exactly matches the right column's `size-10` balancing spacer
      // (`showsExpansionSpacer`) and the centered title stays screen-centered rather than
      // shifted right by the chevron/spacer width mismatch. With a label, grow to content
      // (`shrink` + row) — that path already balances via the wide-back-control `flex-1`
      // columns, so a fixed width would clip the label instead.
      className={
        hasLabel
          ? "h-10 min-w-0 shrink flex-row items-center overflow-hidden rounded-full pr-2 active:bg-accent"
          : "size-10 shrink-0 flex-row items-center justify-center overflow-hidden rounded-full active:bg-accent"
      }
      hitSlop={10}
      onPress={onPress}
      // Stable id for E2E across every placement. The "Back" a11y *text* label is intermittently
      // missing from the release-build hierarchy while a screen transition settles, so a text
      // `tapOn: "Back"` can't find it; the testID (iOS accessibilityIdentifier / Android
      // resource-id) is present as soon as the chevron mounts.
      testID="primary-controls-strip-back-control"
    >
      <Icon
        className={hasLabel ? "-ml-1 text-foreground" : "text-foreground"}
        name="FiChevronLeftBold"
        size={24}
      />
      {hasLabel ? (
        isRenderableAsPlainLabel(label) ? (
          <Text
            className="ml-0.5 min-w-0 shrink text-base text-foreground"
            ellipsizeMode="tail"
            numberOfLines={1}
          >
            {label}
          </Text>
        ) : (
          <View className="ml-0.5 min-w-0 shrink">{label}</View>
        )
      ) : null}
    </Pressable>
  );
}

export type PrimaryControlsStripBackLayout = "app-shell" | "default" | "sidebar";

/**
 * The back control for one lane, rendered only under its gate (`usePrimaryControlsStripBackGate`).
 *
 * Every press goes through `useScopedBack`, so a back control can never dispatch a GO_BACK that
 * crosses the bottom-tab boundary: it pops within the focused tab when history exists, and
 * otherwise navigates to the resolved entry's `fallbackHref`. The bare-chevron fallback (no
 * publisher, so no fallback href) only resolves visible when an in-tab pop is actually possible.
 *
 * In `sidebar` (native landscape) and `app-shell` layouts there isn't enough horizontal room for
 * a screen's label, so only the chevron renders. The app-shell variant is a glass circle that
 * takes the More trigger's slot: nested pages have no header More menu.
 */
export function PrimaryControlsStripBackControl({
  gate: { fallbackHref, label },
  layout = "default",
}: {
  gate: PrimaryControlsStripBackGate;
  layout?: PrimaryControlsStripBackLayout;
}) {
  const { goBack } = useScopedBack();
  const onPress = () => goBack(fallbackHref === undefined ? undefined : { fallbackHref });

  if (layout === "app-shell") {
    return <GlassBackControl onPress={onPress} />;
  }
  return (
    <View className="h-full min-w-0 shrink items-start justify-start">
      <DefaultBackControl label={layout === "sidebar" ? undefined : label} onPress={onPress} />
    </View>
  );
}

export type PrimaryControlsStripBackProps =
  | {
      appShellPlacement?: never;
      children?: never;
      fallbackHref?: never;
      hidden: true;
    }
  | {
      /** Which app-shell lane claims the control: beside the profile trigger, or a screen toolbar. */
      appShellPlacement?: PrimaryControlsStripBackAppShellPlacement;
      /** Label rendered next to the chevron (plain string/number, or a compact custom node). */
      children: ReactNode;
      /**
       * Where the control navigates when there is no in-tab history to pop (deep link, restored
       * state). Required so a rendered back button ALWAYS has a working destination — it either
       * pops within the current bottom tab or navigates here; it can never dispatch an unhandled
       * or cross-tab GO_BACK.
       */
      fallbackHref: Href;
      hidden?: false;
    };

/**
 * Publishes this screen's back control: a label next to the default chevron, plus the mandatory
 * `fallbackHref` the host navigates to when there is nothing in-tab to pop. If no screen renders
 * `PrimaryControlsStripBack`, the host falls back to a bare chevron whenever in-tab history exists. Pass
 * `hidden` to suppress the back control entirely, including that fallback.
 *
 * PUBLISHING IS A REGISTRATION, NOT A RE-PUBLISH. The entry is registered once per mount (at the
 * screen's navigation depth) and thereafter UPDATED in place. An earlier version tore down and
 * re-published on every dependency change, which — because `label` is a `ReactNode` and therefore
 * a fresh object each render — happened on every render, and moved this publisher to the front of
 * a recency-ordered registry. Two publishers are routinely mounted at once (a tab-stack layout and
 * the screen pushed above it, each `useIsFocused()` within its own navigator), so the winner
 * flipped with render order. Registering once and updating in place keeps depth the deciding
 * factor, so the innermost screen wins deterministically.
 */
function PrimaryControlsStripBackNative(props: PrimaryControlsStripBackProps) {
  const isFocused = useIsFocused();
  const registry = useContext(PrimaryControlsStripBackContext);
  const depth = useNavigationDepth();
  const hidden = props.hidden === true;
  const appShellPlacement = hidden ? undefined : props.appShellPlacement;
  const label = hidden ? null : props.children;
  const fallbackHref = hidden ? null : props.fallbackHref;

  // Memoized so the update effect below fires only when the control's CONTENT changes, not on
  // every render. `label` is still an identity-compared `ReactNode`, so a JSX label recomputes
  // this each render — which is correct and cheap, since `update` is in-place and the registry
  // suppresses notifications when the winning entry is unchanged.
  const entry = useMemo<PrimaryControlsStripBackRegistryEntry>(
    () =>
      hidden || fallbackHref === null
        ? PRIMARY_CONTROLS_STRIP_BACK_HIDDEN
        : { appShellPlacement, fallbackHref, label },
    [appShellPlacement, fallbackHref, hidden, label],
  );
  // The registration effect reads the entry through a ref rather than depending on it: `label` is
  // a `ReactNode`, so a JSX label is a new object every render and a dependency on it would
  // re-register the slot on every render — the exact churn that made the winner flip.
  const entryRef = useLatest(entry);
  const handleRef = useRef<ReturnType<typeof registry.publish> | null>(null);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const handle = registry.publish(entryRef.current, { depth });
    handleRef.current = handle;
    return () => {
      handleRef.current = null;
      handle.unpublish();
    };
  }, [depth, entryRef, isFocused, registry]);

  // Push label/href changes into the already-registered slot. `update` keeps the slot's depth and
  // sequence, so a content change can never promote this publisher over a deeper one.
  useEffect(() => {
    handleRef.current?.update(entry);
  }, [entry]);

  return null;
}

export function PrimaryControlsStripBack(props: PrimaryControlsStripBackProps) {
  return isWeb ? null : <PrimaryControlsStripBackNative {...props} />;
}
